import { to } from "@banjoanton/utils";
import { createLogger, ErrorUtil } from "@chesspresso/common";
import { Chess } from "chess.js";
import { parsePgn as _parsePgn } from "chessops/pgn";
import { z } from "zod/v4";
import { type Comment, CommentSchema } from "../models/move-comment";
import { type CaptureSummary, Move, MoveSchema } from "../models/move-model";

const logger = createLogger("chess-service");

export const PgnSummarySchema = z.object({
    moves: z.array(MoveSchema),
    tags: z.record(z.string(), z.string()),
    comments: z.array(CommentSchema),
    pgn: z.string(),
});

export type PgnSummary = z.infer<typeof PgnSummarySchema>;

const parsePgn = (pgn: string): PgnSummary => {
    const [error, parsed] = to(() => {
        // currently, chess.js cannot parse chess960 PGNs correctly, use chessopts in that case to parse at least the tags
        // https://github.com/jhlywa/chess.js/pull/551
        if (pgn.trim().toLowerCase().includes("chess960")) {
            logger.debug("Parsing chess960 PGN with chessops");
            const game = _parsePgn(pgn)?.[0];

            if (!game) {
                throw new Error("Invalid PGN format");
            }

            return {
                moves: [], // skip for chess960 currently
                tags: Object.fromEntries(game.headers),
                comments: [], // skip comments for chess960 currently
            };
        }

        const engine = new Chess();
        engine.loadPgn(pgn);
        const history = engine.history({ verbose: true });
        const headers = engine.getHeaders();
        const comments = engine.getComments();

        const commentsMap: Record<string, Comment[]> = {};
        comments.forEach((comment) => {
            const fen = comment.fen.toLowerCase();
            if (!commentsMap[fen]) {
                commentsMap[fen] = [];
            }

            commentsMap[fen].push({
                fen,
                comment: comment.comment,
            });
        });

        const captureSummary: CaptureSummary = {
            w: { pieces: [], score: 0 },
            b: { pieces: [], score: 0 },
        };

        const moves = history.map((chessJsMove, index) =>
            Move.from(chessJsMove, index, commentsMap, captureSummary)
        );

        return { moves, tags: headers, comments };
    });

    if (error) {
        logger.error({ pgn, error }, "Failed to parse PGN");
        const message = ErrorUtil.toErrorMessage(error);
        throw new Error(`Failed to parse PGN: ${message}`);
    }

    const summary: PgnSummary = {
        tags: parsed.tags,
        moves: parsed.moves,
        comments: parsed.comments,
        pgn,
    };

    return summary;
};

export const ChessService = { parsePgn };
